import { memo, useState, useRef, useEffect } from 'react';
import { METRIC_OPTIONS, METRIC_COLORS } from '../config/metricsConfig';
import styles from './MetricMultiSelect.module.css';

/**
 * Checkbox dropdown. Defaults to METRIC_OPTIONS (the ratio chart's metric
 * set). Pass `options` for a different list — the dropdown keeps that list's
 * order, the "N selected" label, and Escape / outside-click close. A
 * `disabled` option stays visible, is labeled unavailable, and cannot be ticked.
 * Independent from ControlsPanel's single metric <select>.
 */
const MetricMultiSelect = ({ selected, onChange, options }) => {
  const optionList = options || METRIC_OPTIONS;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const toggleMetric = (value) => {
    const option = optionList.find((item) => item.value === value);
    if (option?.disabled) return;
    const next = new Set(selected);
    if (next.has(value)) next.delete(value); else next.add(value);
    // Always re-derive in the option list's fixed order, regardless of click order.
    // Disabled rows stay visible but cannot be ticked.
    onChange(optionList.filter((item) => next.has(item.value) && !item.disabled).map((item) => item.value));
  };

  const selectedCount = optionList.filter((item) => !item.disabled && selected.includes(item.value)).length;

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') setIsOpen(false);
  };

  return (
    <div className={styles.container} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className={styles.toggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {selectedCount} selected ▾
      </button>

      {isOpen && (
        <div className={styles.panel} role="listbox" aria-multiselectable="true">
          {optionList.map(({ value, label, disabled, color }) => (
            <label key={value} className={`${styles.row}${disabled ? ` ${styles.rowDisabled}` : ''}`}>
              <input
                type="checkbox"
                checked={!disabled && selected.includes(value)}
                disabled={!!disabled}
                onChange={() => toggleMetric(value)}
              />
              <span className={styles.swatch} style={{ backgroundColor: color || METRIC_COLORS[value] }} />
              <span>{disabled ? `${label} unavailable` : label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(MetricMultiSelect);
